import { makeAutoObservable, runInAction } from 'mobx'
import { isCancel } from 'axios'
import { RequestCancelAxios } from 'shared/api/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type AnalyticsResponse,
  type IConversionChartResponse,
  type IConversionStatisticResponse,
  type ProcessedStatistic,
  ConversionsAnalyticsApi,
} from 'entities/Analytics'
import { ConversionsTableStore } from 'pages/analytics/pages/conversions/ui/ConversionsTable'
import { calculateTrend, computeHasStatistic } from 'pages/analytics/lib'
import { AnalyticsFiltersStore } from 'pages/analytics/store/analyticsFiltersStore'
import { ExportAnalyticsModalStore } from 'pages/analytics/ui/ExportAnalyticsModal'

export class ConversionsAnalyticsListStore {
  private readonly _analyticsFiltersStore: AnalyticsFiltersStore
  private readonly _exportAnalyticsModalStore: ExportAnalyticsModalStore
  private readonly _conversionsTableStore: ConversionsTableStore
  private readonly _requestCancelAxios = new RequestCancelAxios()

  private _error: string | null = null
  private _pageLoading = false
  private _rawStatistic: IConversionStatisticResponse[] = []

  constructor(entities: IEntitiesStore) {
    this._analyticsFiltersStore = new AnalyticsFiltersStore(entities, {
      onFiltersChange: () => this._fetchAnalytics(),
      additionalFilterTypes: [],
      breakdownFilterTypes: [],
    })
    this._exportAnalyticsModalStore = new ExportAnalyticsModalStore(
      ConversionsAnalyticsApi.exportAnalytics
    )
    this._conversionsTableStore = new ConversionsTableStore(
      () => this._analyticsFiltersStore.analyticsFiltersParams
    )

    makeAutoObservable(this)
  }

  init = () => {
    this._analyticsFiltersStore.initReactions()
    this._fetchAnalytics()
    this._conversionsTableStore.init()
  }

  openExportModal = () => {
    const { analyticsFiltersParams } = this._analyticsFiltersStore
    this._exportAnalyticsModalStore.onOpenExport(analyticsFiltersParams)
  }

  private _fetchAnalytics = async () => {
    runInAction(() => {
      this._pageLoading = true
    })

    try {
      this._error = null
      const { analyticsFiltersParams } = this._analyticsFiltersStore

      this._requestCancelAxios.init()

      await ConversionsAnalyticsApi.preloadAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      const { data } = await ConversionsAnalyticsApi.getAnalytics(analyticsFiltersParams, {
        cancelToken: this._requestCancelAxios.token,
      })

      this.setRawData(data)
    } catch (error) {
      if (!isCancel(error)) {
        this._error = 'Failed to load analytics data'
      }
    } finally {
      runInAction(() => {
        this._pageLoading = false
      })
    }
  }

  setRawData = (
    response: AnalyticsResponse<IConversionStatisticResponse, IConversionChartResponse>
  ) => {
    this._rawStatistic = response.statistic
  }

  get processedStatistic(): ProcessedStatistic[] {
    if (!this._rawStatistic) {
      return []
    }
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  get calendarFilterStore() {
    return this._analyticsFiltersStore.calendarFilterStore
  }

  get conversionsTableStore() {
    return this._conversionsTableStore
  }

  get pageLoading() {
    return this._pageLoading
  }

  get error() {
    return this._error
  }

  dispose = () => {
    this._rawStatistic = []
    this._analyticsFiltersStore.dispose()
    this._requestCancelAxios.reset()
    this._exportAnalyticsModalStore.dispose()
    this._conversionsTableStore.dispose()
  }
}
